import { splitGraphemes } from 'unicode-segmenter/grapheme';

export function sliceGraphemes(
  text: string,
  start: number,
  end?: number,
): string {
  return Array.from(splitGraphemes(text)).slice(start, end).join('');
}

export function truncateGraphemes(
  text: string,
  maxLength: number,
  truncateAt = maxLength,
): string {
  const segments = Array.from(splitGraphemes(text));
  return segments.length > maxLength
    ? `${segments.slice(0, truncateAt).join('')}...`
    : text;
}

export function truncateToCodePointLimit(text: string, limit: number): string {
  let result = '';
  let length = 0;
  for (const segment of splitGraphemes(text)) {
    const segmentLength = Array.from(segment).length;
    if (length + segmentLength > limit) {
      break;
    }
    result += segment;
    length += segmentLength;
  }
  return result;
}

export function normalizeSearchText(text: string): string {
  return text.toLowerCase().normalize('NFC');
}

export function toTitleCase(str: string): string {
  return str
    .toLowerCase()
    .split(' ')
    .map(
      (word) =>
        sliceGraphemes(word, 0, 1).toUpperCase() + sliceGraphemes(word, 1),
    )
    .join(' ');
}

/**
 * Redacts the middle characters of a string with asterisks if it is longer than 4 characters.
 * If the string has 4 characters or fewer, it is returned unchanged.
 *
 * @param str - The input string to be redacted.
 * @returns A string with the first 2 and last 2 characters visible, and the middle redacted with asterisks.
 */
export function redactString(str: string): string {
  if (str.length <= 4) {
    return str;
  }

  const start: string = str.slice(0, 2);
  const end: string = str.slice(-2);

  const asterisks: string = '*'.repeat(str.length - 4);

  return `${start}${asterisks}${end}`;
}

export function cleanQuoteWrapping(
  text: string | null | undefined,
): string | null {
  const trimmed = text?.trim();
  if (!trimmed) {
    return null;
  }
  return trimmed.replace(/^["']|["']$/g, '');
}

export function toAlphanumericLowercase(str: string): string {
  return str
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
}

export function deduplicateStringArrayCaseInsensitive(
  array: string[],
): string[] {
  const seen = new Set<string>();
  const uniqueStrings: string[] = [];
  for (const str of array) {
    const lc = str.toLowerCase();
    if (!seen.has(lc)) {
      seen.add(lc);
      uniqueStrings.push(str);
    }
  }
  uniqueStrings.sort();
  return uniqueStrings;
}

export function safeTrim(str: unknown): string {
  if (typeof str !== 'string') {
    return '';
  }
  return str.trim() || '';
}

export function compareStrings(left: string, right: string): number {
  if (left < right) {
    return -1;
  }
  if (left > right) {
    return 1;
  }
  return 0;
}
